<template>
  <section
    v-if="normalizedUrls.length > 0"
    class="video-section">
    <div class="container">
      <h2 class="video-section__title">
        {{ title }}
      </h2>

      <div
        v-if="normalizedUrls.length === 1"
        class="video-section__wrapper">
        <iframe
          :src="normalizedUrls[0]"
          :title="t('seointer.profession.video_iframe_title', { n: 1 })"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          allowfullscreen
          class="video-section__iframe" />
      </div>

      <div
        v-else
        class="prepeers-video-grid"
        :class="gridModifierClass">
        <div
          v-for="(url, idx) in normalizedUrls"
          :key="`${url}-${idx}`"
          class="prepeers-video-grid__frame">
          <iframe
            :src="url"
            :title="t('seointer.profession.video_iframe_title', { n: idx + 1 })"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
            "
            allowfullscreen
            class="video-section__iframe" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

const props = defineProps({
  /** Titre affiché au-dessus de la zone vidéo (ex. "Nom du métier : explications en vidéo"). */
  title: {
    type: String,
    required: true,
  },
  /** 0 à 3 URLs d'embed ; la section est masquée si le tableau est vide. */
  videoUrls: {
    type: Array as PropType<string[]>,
    default: (): string[] => [],
  },
})

const { t } = useI18n()

/**
 * Uniformise une URL YouTube (watch, youtu.be) en URL d'iframe embed ;
 * conserve les autres URLs telles quelles.
 * @param {string} input URL brute (embed, watch, youtu.be, etc.).
 * @returns {string} URL d'iframe YouTube normalisée, ou l'entrée telle quelle si non reconnue / vide.
 */
function toVideoEmbedUrl(input: string): string {
  const raw = input.trim()
  if (!raw) return ''
  try {
    const parsed = new URL(raw)
    const host = parsed.hostname.replace(/^www\./, '')

    let id: string | null = null
    if (
      host === 'youtube.com' &&
      (parsed.pathname === '/watch' || parsed.pathname.startsWith('/watch'))
    ) {
      id = parsed.searchParams.get('v')
    } else if (host === 'youtu.be') {
      id = parsed.pathname.split('/').filter(Boolean)[0] ?? null
    }

    if (id && /^[\w-]{11}$/.test(id)) {
      return `https://www.youtube.com/embed/${id}?rel=0`
    }

    if (host === 'youtube.com' && parsed.pathname.startsWith('/embed/')) {
      const pathId = parsed.pathname.split('/')[2]
      if (pathId && /^[\w-]{11}$/.test(pathId)) {
        return `https://www.youtube.com/embed/${pathId}?rel=0`
      }
    }
  } catch {
    return raw
  }
  return raw
}

const normalizedUrls = computed((): string[] => {
  if (!Array.isArray(props.videoUrls)) return []
  return props.videoUrls
    .map((u) => toVideoEmbedUrl(String(u ?? '')))
    .filter((u) => u.length > 0)
    .slice(0, 3)
})

const gridModifierClass = computed((): string => {
  const n = normalizedUrls.value.length
  if (n === 2) return 'prepeers-video-grid--two'
  if (n === 3) return 'prepeers-video-grid--three'
  return ''
})
</script>

<style scoped>
/* Grille 2–3 vidéos : pas de max-width globale par carte (contrairement au bloc seul .video-section__wrapper). */
.prepeers-video-grid {
  display: grid;
  gap: var(--space-6);
  width: 100%;
  max-width: var(--container-xl);
  margin: 0 auto;
}

.prepeers-video-grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.prepeers-video-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.prepeers-video-grid__frame {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg), var(--shadow-blue);
}

@media (max-width: 1024px) {
  .prepeers-video-grid--three {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .prepeers-video-grid--two,
  .prepeers-video-grid--three {
    grid-template-columns: 1fr;
  }
}
</style>
